import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
	useSpheraBranchConfigs,
	useSpheraCrConfigs,
	useSpheraOptions,
	useUpdateBranchSpheraLocation,
	useUpdateCrSpheraService,
} from '@/api/queries/sphera-export-queries'
import { SearchInput } from '@/components/commons'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import type { SpheraBranchConfig, SpheraCrConfig } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'

type StatusFilter = 'all' | 'configured' | 'pending'

function StatusDot({ configured }: { configured: boolean }) {
	return (
		<span
			className={`size-2 shrink-0 rounded-full ${configured ? 'bg-green-500' : 'bg-amber-400'}`}
		/>
	)
}

function BranchRow({ branch }: { branch: SpheraBranchConfig }) {
	const { t } = useTranslation()
	const [value, setValue] = useState(branch.spheraLocation ?? '')
	const mutation = useUpdateBranchSpheraLocation()
	const changed = value.trim() !== (branch.spheraLocation ?? '')

	const save = async () => {
		try {
			await mutation.mutateAsync({
				id: branch.id,
				spheraLocation: value.trim() || null,
			})
			toast.success(t('administration.spheraOptions.saved'))
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	return (
		<li className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center">
			<div className="flex min-w-0 flex-1 items-center gap-2">
				<StatusDot configured={!!branch.spheraLocation} />
				<span className="truncate text-sm text-kaefer-dark-grey">
					{branch.name}
				</span>
			</div>
			<div className="sm:w-72 sm:shrink-0">
				<Input
					value={value}
					onChange={(e) => setValue(e.target.value)}
					onKeyDown={(e) => e.key === 'Enter' && changed && save()}
					placeholder={t('administration.spheraOptions.locationPlaceholder')}
				/>
			</div>
			<Button
				size="sm"
				className="w-auto shrink-0"
				disabled={!changed || mutation.isPending}
				onClick={save}
			>
				{t('administration.spheraOptions.save')}
			</Button>
		</li>
	)
}

function CrRow({ cr, services }: { cr: SpheraCrConfig; services: string[] }) {
	const { t } = useTranslation()
	const [value, setValue] = useState(cr.spheraService ?? '')
	const mutation = useUpdateCrSpheraService()
	const changed = value !== (cr.spheraService ?? '')

	const save = async () => {
		try {
			await mutation.mutateAsync({
				id: cr.id,
				spheraService: value || null,
			})
			toast.success(t('administration.spheraOptions.saved'))
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	return (
		<li className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center">
			<div className="flex min-w-0 flex-1 items-center gap-2">
				<StatusDot configured={!!cr.spheraService} />
				<div className="min-w-0">
					<p className="text-sm text-kaefer-dark-grey" title={cr.name}>
						<span className="font-semibold">{cr.code}</span> · {cr.name}
					</p>
					<p className="text-xs text-muted-foreground">{cr.branchName}</p>
				</div>
			</div>
			<div className="sm:w-72 sm:shrink-0">
				<NativeSelect value={value} onChange={(e) => setValue(e.target.value)}>
					<NativeSelectOption value="">
						{t('administration.spheraOptions.notConfigured')}
					</NativeSelectOption>
					{services.map((service) => (
						<NativeSelectOption key={service} value={service}>
							{service}
						</NativeSelectOption>
					))}
				</NativeSelect>
			</div>
			<Button
				size="sm"
				className="w-auto shrink-0"
				disabled={!changed || mutation.isPending}
				onClick={save}
			>
				{t('administration.spheraOptions.save')}
			</Button>
		</li>
	)
}

function ListFilters({
	search,
	onSearch,
	status,
	onStatus,
}: {
	search: string
	onSearch: (value: string) => void
	status: StatusFilter
	onStatus: (value: StatusFilter) => void
}) {
	const { t } = useTranslation()
	return (
		<div className="flex flex-col gap-2 sm:flex-row">
			<div className="flex-1">
				<SearchInput
					value={search}
					onChange={onSearch}
					placeholder={t('administration.spheraOptions.search')}
				/>
			</div>
			<div className="sm:w-48">
				<NativeSelect
					value={status}
					onChange={(e) => onStatus(e.target.value as StatusFilter)}
				>
					<NativeSelectOption value="all">
						{t('administration.spheraOptions.filterAll')}
					</NativeSelectOption>
					<NativeSelectOption value="configured">
						{t('administration.spheraOptions.filterConfigured')}
					</NativeSelectOption>
					<NativeSelectOption value="pending">
						{t('administration.spheraOptions.filterPending')}
					</NativeSelectOption>
				</NativeSelect>
			</div>
		</div>
	)
}

function matches(status: StatusFilter, configured: boolean) {
	return (
		status === 'all' || (status === 'configured' ? configured : !configured)
	)
}

export function SpheraBranchList() {
	const { t } = useTranslation()
	const { data, isLoading } = useSpheraBranchConfigs()
	const [search, setSearch] = useState('')
	const [status, setStatus] = useState<StatusFilter>('all')
	const q = search.trim().toLowerCase()

	const items = (data ?? []).filter(
		(branch) =>
			matches(status, !!branch.spheraLocation) &&
			(!q ||
				branch.name.toLowerCase().includes(q) ||
				(branch.spheraLocation ?? '').toLowerCase().includes(q)),
	)

	return (
		<div className="space-y-3">
			<p className="text-xs text-muted-foreground">
				{t('administration.spheraOptions.branchesHint')}
			</p>
			<ListFilters
				search={search}
				onSearch={setSearch}
				status={status}
				onStatus={setStatus}
			/>
			{isLoading ? (
				<p className="p-4 text-center text-sm text-muted-foreground">
					{t('common.loading')}
				</p>
			) : items.length === 0 ? (
				<p className="p-4 text-center text-sm text-muted-foreground">
					{t('administration.spheraOptions.emptyList')}
				</p>
			) : (
				<ul className="divide-y rounded-md border px-3">
					{items.map((branch) => (
						<BranchRow
							key={`${branch.id}-${branch.spheraLocation}`}
							branch={branch}
						/>
					))}
				</ul>
			)}
		</div>
	)
}

export function SpheraCrList() {
	const { t } = useTranslation()
	const { data, isLoading } = useSpheraCrConfigs()
	const { data: options } = useSpheraOptions()
	const [search, setSearch] = useState('')
	const [status, setStatus] = useState<StatusFilter>('all')
	const q = search.trim().toLowerCase()
	const services = (options?.services ?? []).map((service) => service.name)

	const items = (data ?? []).filter(
		(cr) =>
			matches(status, !!cr.spheraService) &&
			(!q ||
				cr.code.toLowerCase().includes(q) ||
				cr.name.toLowerCase().includes(q) ||
				cr.branchName.toLowerCase().includes(q) ||
				(cr.spheraService ?? '').toLowerCase().includes(q)),
	)

	return (
		<div className="space-y-3">
			<p className="text-xs text-muted-foreground">
				{t('administration.spheraOptions.crsHint')} ({items.length})
			</p>
			<ListFilters
				search={search}
				onSearch={setSearch}
				status={status}
				onStatus={setStatus}
			/>
			{isLoading ? (
				<p className="p-4 text-center text-sm text-muted-foreground">
					{t('common.loading')}
				</p>
			) : items.length === 0 ? (
				<p className="p-4 text-center text-sm text-muted-foreground">
					{t('administration.spheraOptions.emptyList')}
				</p>
			) : (
				<ul className="divide-y rounded-md border px-3">
					{items.map((cr) => (
						<CrRow
							key={`${cr.id}-${cr.spheraService}`}
							cr={cr}
							services={services}
						/>
					))}
				</ul>
			)}
		</div>
	)
}
